import "./style.scss";
import { RefresherEventDetail } from "@ionic/core";
import { IonPage, IonRefresher, IonRefresherContent } from "@ionic/react";
import { LinearProgress } from "@mui/material";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { AppBarHeader, BackButtonLink, PageWithHeader } from "@src/appV2/lib";
import { SUPPORT_LINKS } from "@src/constants/DEFAULT_SUPPORT_LINKS";

import { useAttendanceScoreProfile } from "./api/useAttendanceScoreProfile";
import { AttendanceScorePolicyDetailCard } from "./attendanceScorePolicyDetailCard";
import { LearnMoreCard } from "./learnMoreCard";
import { MyAttendanceScoreContainer } from "./myAttendanceScoreContainer";
import { TabRouterPath } from "../routing/constant/tabRoute";
import { useSession } from "../store/helperHooks";

export function AttendanceScoreRatingPage() {
  const { agent } = useSession();

  const attendanceScorePolicyLinks = useCbhFlag(CbhFeatureFlag.ATTENDANCE_SCORE_POLICY_LINKS, {
    defaultValue: {},
  });

  const { isLoading, data, isSuccess, refetch } = useAttendanceScoreProfile(agent?.userId, {
    meta: { userErrorMessage: "Something went wrong while loading your attendance score." },
  });

  const statusPolicyLink = {
    label:
      attendanceScorePolicyLinks?.understandingAccountStatus?.label ??
      "Understanding Your Account Status",
    url:
      attendanceScorePolicyLinks?.understandingAccountStatus?.url ?? SUPPORT_LINKS.ACCOUNT_STATUS,
  };

  return (
    <IonPage>
      <PageWithHeader
        appBarHeader={
          <AppBarHeader
            title="My Attendance Score"
            leftCta={<BackButtonLink defaultBackTo={TabRouterPath.RATINGS} />}
          />
        }
        containerVariant="without-margin"
        containerMaxWidth="md"
        onRefresh={refetch}
      >
        <IonRefresher
          slot="fixed"
          onIonRefresh={(refreshEvent?: CustomEvent<RefresherEventDetail>) => {
            refetch();
            refreshEvent?.detail.complete();
          }}
        >
          <IonRefresherContent />
        </IonRefresher>

        {(!agent || isLoading) && (
          <LinearProgress sx={{ height: "4px" }} data-testid="attendance-score-progressbar" />
        )}

        {isSuccess && agent && (
          <>
            <MyAttendanceScoreContainer
              score={data.score}
              accountStatus={data.accountStatus}
              policyDetailUrl={
                attendanceScorePolicyLinks?.howAttendanceWorks?.url ||
                SUPPORT_LINKS.ATTENDANCE_SCORE
              }
              tmz={agent.tmz}
            />
            {data.policy && <AttendanceScorePolicyDetailCard policy={data.policy} />}
            <LearnMoreCard
              links={[
                {
                  label:
                    attendanceScorePolicyLinks?.howAttendanceWorks?.label ??
                    "How the Attendance Score Works",
                  url:
                    attendanceScorePolicyLinks?.howAttendanceWorks?.url ||
                    SUPPORT_LINKS.ATTENDANCE_SCORE,
                },
                statusPolicyLink,
                {
                  label:
                    attendanceScorePolicyLinks?.doNotReturnPolicy?.label ??
                    "Do Not Return (DNR) Policy",
                  url:
                    attendanceScorePolicyLinks?.doNotReturnPolicy?.url ||
                    SUPPORT_LINKS.DNR_REQUESTS_WORKER,
                },
              ]}
            />
          </>
        )}
      </PageWithHeader>
    </IonPage>
  );
}
